'use client';

import { useState, createContext, useContext, ReactNode, useCallback, useMemo, useEffect } from 'react';
import { useFirebase } from './firebase';

// Types
import { UserProfile } from '@/types';

// Actions
import { getAllSimpleUsers } from '@/actions/client';

// Utils
import { showErrorToast } from '@/utils';
interface TrainingFieldEditContextValue {
  isLoading: boolean;
  users: UserProfile[] | null;
  isFieldActive: (fieldId: string) => boolean;
  activateField: (fieldId: string) => void;
  deactivateField: () => void;
}
const TrainingFieldEditContext = createContext<TrainingFieldEditContextValue | undefined>(undefined);

/**
 * Hook to access training field edit context
 * @returns Training field edit context value, or null if context is not available
 */
export const useTrainingFieldEdit = (): TrainingFieldEditContextValue | null => {
  return useContext(TrainingFieldEditContext) ?? null;
};
interface TrainingFieldEditProviderProps {
  children: ReactNode;
}

/**
 * Provider component for managing active training field edit state
 * Tracks which training field is currently being edited to ensure only one field
 */
export const TrainingFieldEditProvider = ({
  children
}: TrainingFieldEditProviderProps) => {
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [users, setUsers] = useState<UserProfile[] | null>([]);
  const [isLoading, setIsLoading] = useState(false);
  const {
    getToken
  } = useFirebase();
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const {
        data,
        error
      } = await getAllSimpleUsers({
        requestConfig: {
          token
        }
      });
      if (error) {
        showErrorToast(error);
        return;
      }
      setUsers(data);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  }, [getToken]);
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, getToken]);
  const isFieldActive = useCallback((fieldId: string) => activeFieldId === fieldId, [activeFieldId]);
  const activateField = useCallback((fieldId: string) => {
    setActiveFieldId(fieldId);
  }, []);
  const deactivateField = useCallback(() => {
    setActiveFieldId(null);
  }, []);
  const value: TrainingFieldEditContextValue = useMemo(() => ({
    isFieldActive,
    activateField,
    deactivateField,
    users,
    isLoading
  }), [isFieldActive, activateField, deactivateField, users, isLoading]);
  return <TrainingFieldEditContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="TrainingFieldEditProvider" data-sentry-source-file="training.tsx">
      {children}
    </TrainingFieldEditContext.Provider>;
};